import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { CircleNotch, Info } from "@phosphor-icons/react";

/**
 * Compact confidence pill for a bot — fetches ``GET /api/bots/{id}/confidence``
 * on mount (backend caches for 24h so re-mounting is cheap) and renders a
 * colour-coded percentage. Tooltip shows the underlying win_rate + alpha.
 */
export default function BotConfidenceBadge({ botId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/bots/${botId}/confidence`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [botId]);

  if (loading) {
    return (
      <span
        data-testid={`bot-confidence-${botId}`}
        className="mono text-[9px] uppercase tracking-widest text-zinc-500 inline-flex items-center gap-1"
      >
        <CircleNotch size={9} className="animate-spin" /> confidence
      </span>
    );
  }

  if (!data || data.score == null) {
    return (
      <span
        data-testid={`bot-confidence-${botId}`}
        title={data?.reason || "Confidence unavailable"}
        className="mono text-[9px] uppercase tracking-widest text-zinc-500 inline-flex items-center gap-1"
      >
        <Info size={9} /> confidence: —
      </span>
    );
  }

  const s = data.score;
  const band =
    data.band ||
    (s >= 75 ? "high" : s >= 50 ? "medium" : "low");
  const bandClass =
    band === "high"
      ? "text-[#00C805] bg-[#00C805]/15 border-[#00C805]/30"
      : band === "medium"
      ? "text-amber-400 bg-amber-500/10 border-amber-500/30"
      : "text-[#FF3B30] bg-[#FF3B30]/10 border-[#FF3B30]/30";

  const tip =
    `${band.toUpperCase()} — ` +
    `${data.win_rate}% win-rate over ${data.num_sells} closed signals, ` +
    `${data.alpha_pct >= 0 ? "+" : ""}${data.alpha_pct}% vs buy&hold, ` +
    `-${data.max_drawdown_pct}% max drawdown`;

  return (
    <span
      data-testid={`bot-confidence-${botId}`}
      data-band={band}
      title={tip}
      className={`mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm border font-bold ${bandClass}`}
    >
      {s}% confidence
    </span>
  );
}
